import { DOG_AREAS, isUsableSize, sizeGateApplies, unsizedMessage, type BookingDetail, type Owner } from '@bbbk/schema';

// The per-dog area rules, out of the modal so they can be tested.
//
// BookingForm renders with `renderToStaticMarkup` in this suite — no effects,
// no clicks — so a rule left inline in the component is a rule with no test.
// These three are the whole of dogs-in-areas' desk behaviour: what the panel
// starts with, what a chip does, and what stops a save.

export type DogAreaMap = Record<string, string | null>;

/**
 * What the panel opens with.
 *
 * An EDIT seeds from what is stored, NULLs included — a historical booking may
 * hold them (AC3's residue: 253 dogs across 128 bookings nobody can now assign
 * — 125 of them already in the past),
 * and AC11 is what refuses to save over one. Seeding those to a default would
 * quietly invent a room for a dog nobody placed, which is the exact mistake
 * this story exists to undo.
 *
 * A NEW booking starts every dog on the chip, because a new booking has no
 * history to be honest about.
 */
export function seedDogAreas(
  dogs: readonly { id: string }[],
  booking: Pick<BookingDetail, 'dogs'> | null | undefined,
  fallback: string,
): DogAreaMap {
  const seeded: DogAreaMap = {};
  for (const d of dogs) {
    const stored = booking?.dogs?.find((bd) => bd.id === d.id)?.area ?? null;
    seeded[d.id] = stored ?? (booking ? null : fallback);
  }
  return seeded;
}

/**
 * What a chip does — AC10, and Freddi's own words on 2026-09-10: "if big little
 * or house is selected all dogs that belong to that household are in that area".
 *
 * The chip is NOT a filter over the panel. It MOVES every dog, and picking a
 * real area after fiddling with the rows discards those choices deliberately:
 * Patricia has just said they are all in one place. The server applies the same
 * rule on write, so a stale tab cannot store a booking whose chip and rows
 * disagree.
 *
 * `other` changes nothing — it opens the panel and leaves the rows as they are.
 */
export function applyChip(
  dogs: readonly { id: string }[],
  chip: string,
  current: DogAreaMap,
): DogAreaMap {
  if (chip === 'other') return current;
  return Object.fromEntries(dogs.map((d) => [d.id, chip]));
}

/**
 * The dogs with nowhere to be — AC11's refusal, which names them.
 *
 * Only meaningful under `other`: any other chip has just placed every dog by
 * definition, so an unplaced dog cannot exist there.
 */
export function unplacedDogs<T extends { id: string }>(
  dogs: readonly T[],
  areas: DogAreaMap,
): T[] {
  return dogs.filter((d) => !areas[d.id]);
}

/** The blocker sentence, or null when nothing blocks. */
export function unplacedBlocker(
  dogs: readonly { id: string; name: string }[],
  chip: string,
  areas: DogAreaMap,
): string | null {
  if (chip !== 'other') return null;
  const stuck = unplacedDogs(dogs, areas);
  if (stuck.length === 0) return null;
  const names = stuck.map((d) => d.name).join(' and ');
  return `${names} ${stuck.length === 1 ? 'needs an area' : 'need areas'} before this booking can be saved.`;
}


/**
 * The dogs with no usable size — the gate that makes the leak self-heal.
 *
 * Freddi, 2026-09-11: "once we try to book them in in future we will be unable
 * to without setting the size so we can just do that when they book in next."
 *
 * THAT WAS NOT TRUE WHEN HE SAID IT, which is the whole reason this exists.
 * Nothing in the booking validator or the modal looked at size, and 98 unsized
 * dogs had been booked onto 387 bookings in the previous 180 days. Without a
 * gate the plan is a hope: the dogs keep being booked and keep being invisible
 * to availability.
 *
 * Capacity counts SIZE and nothing else (Freddi, same day), so a dog with no
 * size is a dog no availability sum can see. `house` counts as unsized here —
 * it is an area and never a size.
 *
 * Scoped to the SAVE, not to opening the booking: a past stay stays readable.
 * Raised as a toast rather than a footer blocker (Freddi, 2026-09-11): the fix
 * is on the DOG, not on this form, so a permanently-disabled Save would tell
 * Patricia she is stuck without telling her where to go.
 */
/**
 * The dogs the client-side gate reads: on an edit the BOOKING's own roster,
 * because that is what the server's PATCH checks and a booking frozen at
 * import can differ from the household today; on a create the household's,
 * because the booking does not exist yet (review on #239).
 */
export function gateRoster(
  booking: { dogs: readonly { id: string; name: string; size?: string | null }[] } | null | undefined,
  owner: { dogs: readonly { id: string; name: string; size?: string | null }[] },
): readonly { id: string; name: string; size?: string | null }[] {
  return booking ? booking.dogs : owner.dogs;
}

export function unsizedBlocker(
  dogs: readonly { id: string; name: string; size?: string | null }[],
  move: { from: string | null; to: string },
): string | null {
  // THE SAME RULE AS THE SERVER (dog-size-gate-everywhere AC3/AC3b): a save
  // is refused only when it carries the booking INTO the gate — a new booking
  // at a gated status, or a request / cancellation moving to one. A booking
  // already inside moves freely: Arrived, Collected, a re-save. Checked here
  // first so Patricia is told without a round trip; the server refuses
  // regardless.
  if (!sizeGateApplies(move.to)) return null;
  if (move.from !== null && sizeGateApplies(move.from)) return null;
  const stuck = dogs.filter((d) => !isUsableSize(d.size));
  if (stuck.length === 0) return null;
  // The bare sentence. The form prefixes "Unable to save —" once; this used to
  // carry the prefix as well, and the toast said it twice.
  return unsizedMessage(stuck.map((d) => d.name));
}

/** Every value the panel may set — re-exported so the modal has one import. */
export const PER_DOG_AREAS = DOG_AREAS;

export type { Owner };
